import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Tag, Lock } from "lucide-react";

const KEY = "gtg_exit_offer_shown";

// One-time exit-intent reminder of the auto-applied GHOST20 discount.
// Fires when the cursor leaves the top of the viewport (toward the tab bar).
export const ExitOffer = ({ onClaim }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(KEY) === "1") return;
    let armed = false;
    let done = false;
    const arm = setTimeout(() => { armed = true; }, 4000);

    const trigger = () => {
      if (!armed || done) return;
      done = true;
      localStorage.setItem(KEY, "1");
      setOpen(true);
      cleanup();
    };

    // Fires when the pointer leaves the top edge of the viewport (toward tabs / close).
    const onMouseOut = (e) => {
      if (!e.relatedTarget && (e.clientY === undefined || e.clientY <= 8)) trigger();
    };
    // Fallback: any time the pointer leaves the document near the top.
    const onLeave = (e) => {
      if ((e.clientY === undefined || e.clientY <= 8)) trigger();
    };

    const cleanup = () => {
      document.removeEventListener("mouseout", onMouseOut);
      document.documentElement.removeEventListener("mouseleave", onLeave);
    };

    document.addEventListener("mouseout", onMouseOut);
    document.documentElement.addEventListener("mouseleave", onLeave);
    return () => { clearTimeout(arm); cleanup(); };
  }, []);

  const close = () => setOpen(false);
  const claim = () => { close(); onClaim?.(); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="exit-offer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[110] flex items-center justify-center px-4"
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={close} />
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 w-full max-w-md border border-accent/30 bg-[#0a0a0a] p-8 lg:p-10 text-center"
          >
            <button
              type="button"
              data-testid="exit-offer-close"
              onClick={close}
              aria-label="Close"
              className="absolute top-5 right-5 text-white/40 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>

            <span className="font-mono text-xs uppercase tracking-[0.3em] text-accent">// Before you vanish</span>
            <h3 className="mt-4 font-display font-black uppercase tracking-tighter text-3xl leading-[0.95]">
              Your 20% off<br />is still ready.
            </h3>
            <p className="mt-5 font-body text-sm text-white/55 leading-relaxed">
              Don't leave money on the grid. Code <span className="text-accent font-bold">GHOST20</span> is applied automatically, no typing, no catch.
            </p>

            <div className="mt-6 inline-flex items-center gap-2 border border-accent/30 bg-accent/[0.06] px-4 py-2.5">
              <Tag size={14} className="text-accent" />
              <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-white/70">GHOST20 · 20% off · one-time pack</span>
            </div>

            <button
              type="button"
              data-testid="exit-offer-claim"
              onClick={claim}
              className="group relative mt-7 w-full inline-flex items-center justify-center gap-3 bg-accent text-white font-mono uppercase tracking-[0.15em] text-sm px-8 py-5 overflow-hidden"
            >
              <span className="absolute inset-0 bg-white translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]" />
              <span className="relative z-10 flex items-center gap-2 group-hover:text-black transition-colors duration-300">
                <Lock size={14} /> Claim 20% Off
              </span>
            </button>
            <button
              type="button"
              data-testid="exit-offer-dismiss"
              onClick={close}
              className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-white/30 hover:text-white/60 transition-colors"
            >
              No thanks, I'll pay full price
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
